import { GlobalEventType } from '@/constants'
import { mapGetters } from 'vuex'
import { keepAliveRoutes } from '@/config'
import { getPrintTemplateList, handlePrintData, handleUpdataPrintData } from '@/api/set/printTemplateApi'
import i18n from '@/locale'
import modules from '@/views/set/printTemplate/config'


/**
 * 关闭页面返回刷新列表
 */
export const CloseToRefreshList = {
  /**
   * 路由离开时，设置目标页是否需要刷新列表
   * @param {*} to
   * @param {*} from
   * @param {*} next
   */
  beforeRouteLeave(to, from, next) {
    const fromNeedRefreshList = from.meta?.needRefreshList
    // needRefreshList 没有定义在data中，是因为它只作为一个普通变量，无需响应式
    if (!this?.needRefreshList && !fromNeedRefreshList) return next()
    to.meta.needRefreshList = this?.needRefreshList || fromNeedRefreshList
    from.meta.needRefreshList = false
    next()
  },
  methods: {
    /**
     * 关闭当前页（跳转到指定页面）
     * @param {string | Location} to 可选参数,与路由对象的`push`方法入参一致，如果未指定 `to`,则默认打开前一个标签
     * @example
     * this.closeSelf('/xxx')
     * this.closeSelf(this.$route)
     * @returns
     */
    /**
     * 当前账号应进入的收银首页（旧 /cashier 或新 /newCashier，互斥权限）
     */
    getCashierHomePath() {
      return this.$store.getters['user/hasNewCashier'] ? '/newCashier' : '/cashier'
    },
    getCashierHomeRouteName() {
      return this.$store.getters['user/hasNewCashier'] ? 'NewCashier' : 'Cashier'
    },
    isCashierRouteTarget(to) {
      if (!to || to instanceof Event) return false
      if (typeof to === 'string') {
        return (
          to === '/cashier' ||
          to.startsWith('/cashier?') ||
          to === '/newCashier' ||
          to.startsWith('/newCashier?')
        )
      }
      return (
        to.name === 'Cashier' ||
        to.name === 'NewCashier' ||
        to.path === '/cashier' ||
        to.path === '/newCashier'
      )
    },
    replaceToCashier() {
      const name = this.getCashierHomeRouteName()
      const path = this.getCashierHomePath()
      const resolved = this.$router.resolve({ name })
      const hasNamedRoute = resolved.route.matched.some((record) => record.name === name)
      if (hasNamedRoute) {
        return this.$router.replace({ name }).catch(() => {})
      }
      return this.$router.replace({ path }).catch(() => {})
    },
    /** 业务页跳转收银首页（按权限分流） */
    goCashierHome(replace = false) {
      const location = { path: this.getCashierHomePath() }
      return replace
        ? this.$router.replace(location).catch(() => {})
        : this.$router.push(location).catch(() => {})
    },
    async closeSelf(to) {
      await this.$mgCloseEditPageConfirm(this.$route.name)
      const visiteRoutes = this.$store.getters['tabsBar/visiteRoutes']

      // 结算页为顶层路由，返回收银固定 replace，避免 history.back 落到无效记录触发 404
      if (
        this.$route.name === 'payPanel' ||
        this.$route.path === '/payPanel' ||
        this.$route.name === 'newPayPanel' ||
        this.$route.path === '/newPayPanel'
      ) {
        await this.$store.dispatch('tabsBar/removeTab', this.$route)
        return this.replaceToCashier()
      }

      if (to && !(to instanceof Event)) {
        if (this.isCashierRouteTarget(to)) {
          await this.$store.dispatch('tabsBar/removeTab', this.$route)
          return this.replaceToCashier()
        }

        let location = typeof to === 'string' ? this.$router.match(to) : to
        if (location.path !== this.$route.path) {
          const targetRoute = visiteRoutes.find((item) => item.path === location.path)
          if (targetRoute) location = targetRoute
          const resolved = this.$router.resolve(location)
          if (!resolved.route.matched.length) {
            console.warn('TIPS closeSelf 目标路由无效，回退收银页:', location)
            await this.$store.dispatch('tabsBar/removeTab', this.$route)
            return this.replaceToCashier()
          }
          await this.$store.dispatch('tabsBar/removeTab', this.$route)
          return this.$router.push(location).catch(() => {})
        }
      }

      // 未指定目标：仅在有有效历史时 back，否则回收银（防止偶发 404）
      if (window.history.length > 1) {
        return this.$router.back()
      }
      return this.replaceToCashier()
    },
  },
}

// GlobalKeyboardMixins.js

// **通用方法，只定义一次**
const commonMethods = {
  /**
   * 判断是否可以执行操作
   * - 避免弹窗打开时触发快捷键
   */
  canExecuteAction() {
    const myElement = document.getElementsByClassName('el-message-box__wrapper')[0]
    if (myElement && myElement?.style?.display !== 'none') {
      return false
    }
    return true
  },

  /**
   * 统一封装方法，执行前做拦截
   * @param {Function} actionFn 需要执行的函数
   */
  wrapKeyboardAction(actionFn) {
    return (...args) => {
      if (!this.canExecuteAction()) {
        return
      }
      actionFn.apply(this, args)
    }
  },

  /**
   * 自动封装 `keyboard` 开头的方法
   */
  wrapAllKeyboardActions() {
    Object.keys(this).forEach((key) => {
      if (key.startsWith('keyboard') && typeof this[key] === 'function') {
        this[key] = this.wrapKeyboardAction(this[key])
      }
    })
  },
}

/**
 * 全局快捷键操作-列表页
 */
export const GlobalKeyboardListActionMixin = {
  activated() {
    this.wrapAllKeyboardActions() // 自动封装 `keyboard` 方法
    // 使用 keep-alive 缓存
    if (keepAliveRoutes.includes(this.$route.name)) {
      this.$GlobalEventBus.on(GlobalEventType.ADD, this.keyboardAddAction)
      this.$GlobalEventBus.on(GlobalEventType.SAVE, this.keyboardSaveAction)
      this.$GlobalEventBus.on(GlobalEventType.RECORDING, this.keyboardRecordingAction)
      this.$GlobalEventBus.on(GlobalEventType.PRINT, this.keyboardPrintAction)
      this.$GlobalEventBus.on(GlobalEventType.CANCEL, this.keyboardCancelAction)
      this.$GlobalEventBus.on(GlobalEventType.UP, this.keyboardUpAction)
      this.$GlobalEventBus.on(GlobalEventType.DOWN, this.keyboardDownAction)
      this.$GlobalEventBus.on(GlobalEventType.ENABLE, this.keyboardEnableAction)
      this.$GlobalEventBus.on(GlobalEventType.REMOVE, this.keyboardRemoveAction)
      this.$GlobalEventBus.on(GlobalEventType.CUSTOMER_CHANGE, this.keyboardCustomerChange)
      this.$once('hook:deactivated', () => {
        this.$GlobalEventBus.off(GlobalEventType.ADD, this.keyboardAddAction)
        this.$GlobalEventBus.off(GlobalEventType.SAVE, this.keyboardSaveAction)
        this.$GlobalEventBus.off(GlobalEventType.RECORDING, this.keyboardRecordingAction)
        this.$GlobalEventBus.off(GlobalEventType.PRINT, this.keyboardPrintAction)
        this.$GlobalEventBus.off(GlobalEventType.CANCEL, this.keyboardCancelAction)
        this.$GlobalEventBus.off(GlobalEventType.UP, this.keyboardUpAction)
        this.$GlobalEventBus.off(GlobalEventType.DOWN, this.keyboardDownAction)
        this.$GlobalEventBus.off(GlobalEventType.ENABLE, this.keyboardEnableAction)
        this.$GlobalEventBus.off(GlobalEventType.REMOVE, this.keyboardRemoveAction)
        this.$GlobalEventBus.off(GlobalEventType.CUSTOMER_CHANGE, this.keyboardCustomerChange)
      })
    }
  },

  mounted() {
    this.wrapAllKeyboardActions() // 自动封装 `keyboard` 方法
    // 未使用 keep-alive 缓存
    if (!keepAliveRoutes.includes(this.$route.name)) {
      this.$GlobalEventBus.on(GlobalEventType.ADD, this.keyboardAddAction)
      this.$GlobalEventBus.on(GlobalEventType.SAVE, this.keyboardSaveAction)
      this.$GlobalEventBus.on(GlobalEventType.RECORDING, this.keyboardRecordingAction)
      this.$GlobalEventBus.on(GlobalEventType.PRINT, this.keyboardPrintAction)
      this.$GlobalEventBus.on(GlobalEventType.CANCEL, this.keyboardCancelAction)
      this.$GlobalEventBus.on(GlobalEventType.UP, this.keyboardUpAction)
      this.$GlobalEventBus.on(GlobalEventType.DOWN, this.keyboardDownAction)
      this.$GlobalEventBus.on(GlobalEventType.ENABLE, this.keyboardEnableAction)
      this.$GlobalEventBus.on(GlobalEventType.REMOVE, this.keyboardRemoveAction)
      this.$GlobalEventBus.on(GlobalEventType.CUSTOMER_CHANGE, this.keyboardCustomerChange)
      this.$once('hook:beforeDestroy', () => {
        this.$GlobalEventBus.off(GlobalEventType.ADD, this.keyboardAddAction)
        this.$GlobalEventBus.off(GlobalEventType.SAVE, this.keyboardSaveAction)
        this.$GlobalEventBus.off(GlobalEventType.RECORDING, this.keyboardRecordingAction)
        this.$GlobalEventBus.off(GlobalEventType.PRINT, this.keyboardPrintAction)
        this.$GlobalEventBus.off(GlobalEventType.CANCEL, this.keyboardCancelAction)
        this.$GlobalEventBus.off(GlobalEventType.UP, this.keyboardUpAction)
        this.$GlobalEventBus.off(GlobalEventType.DOWN, this.keyboardDownAction)
        this.$GlobalEventBus.off(GlobalEventType.ENABLE, this.keyboardEnableAction)
        this.$GlobalEventBus.off(GlobalEventType.REMOVE, this.keyboardRemoveAction)
        this.$GlobalEventBus.off(GlobalEventType.CUSTOMER_CHANGE, this.keyboardCustomerChange)
      })
    }
  },

  methods: {
    // 新增
    keyboardAddAction() {
      if (!this.$refs.pane.$refs.add?.disabled) {
        this.$refs.pane.$refs.add?.$el.dispatchEvent(new Event('click'))
      }
    },
    // 保存
    keyboardSaveAction() {
      const saveRef = this.$refs.detail?.$refs?.save
      if (!saveRef?.disabled) {
        saveRef?.$el?.dispatchEvent(new Event('click'))
      }
    },
    // 入账
    keyboardRecordingAction() {
      const recordingRef = this.$refs.detail?.$refs?.recording
      if (!recordingRef?.disabled) {
        recordingRef?.$el?.dispatchEvent(new Event('click'))
      }
    },
    // 打印
    keyboardPrintAction() {
      const printRef = this.$refs.detail?.$refs?.print
      if (!printRef?.disabled) {
        printRef?.$el?.dispatchEvent(new Event('click'))
      }
    },
    // 返回列表/取消
    keyboardCancelAction() {
      const cancelRef = this.$refs.detail?.$refs?.cancel
      if (!cancelRef?.disabled) {
        cancelRef?.$el?.dispatchEvent(new Event('click'))
      }
    },
    // 切换上一个
    keyboardUpAction() {
      if (this.$refs.pane) {
        this.$refs.pane.selectUp()
      }
    },

    // 切换下一个
    keyboardDownAction() {
      if (this.$refs.pane) {
        this.$refs.pane.selectDown()
      }
    },
    // 启用
    keyboardEnableAction() {
      const enableRef = this.$refs.detail?.$refs?.enable
      if (!enableRef?.disabled) {
        enableRef?.$el?.dispatchEvent(new Event('click'))
      }
    },

    // 启用
    keyboardRemoveAction() {
      const removeRef = this.$refs.detail?.$refs?.remove
      if (!removeRef?.disabled) {
        removeRef?.$el?.dispatchEvent(new Event('click'))
      }
    },

    // 更换客户
    keyboardCustomerChange() {
      console.log(!this.$refs.customerChange?.disabled, this.$refs.customerChange)
      if (!this.$refs.customerChange?.disabled) {
        this.$refs.customerChange?.$el.dispatchEvent(new Event('click'))
      }
    },
  },
}
// **给 mixin 添加通用方法**
Object.assign(GlobalKeyboardListActionMixin.methods, commonMethods)
/**
 * 全局快捷键操作-操作页
 */
export const GlobalKeyboardDetailActionMixin = {
  activated() {
    this.wrapAllKeyboardActions() // 自动封装 `keyboard` 方法
    // 2026/09/20 原因：采购入库「录入更多信息」会 addDynamicAliveRoutes 把本页收进缓存。
    // 旧逻辑只认静态 keepAliveRoutes，动态缓存后再回来不会重新绑定 ESC/保存。
    // 旧代码：
    // if (keepAliveRoutes.includes(this.$route.name)) {
    //   this.$GlobalEventBus.on(GlobalEventType.CANCEL, this.keyboardCancelAction)
    //   this.$GlobalEventBus.on(GlobalEventType.SAVE, this.keyboardSaveAction)
    //   this.$GlobalEventBus.on(GlobalEventType.RECORDING, this.keyboardRecordingAction)
    //   this.$GlobalEventBus.on(GlobalEventType.FAST_ADD, this.keyboardFastAddAction)
    //   this.$once('hook:deactivated', () => {
    //     this.$GlobalEventBus.off(GlobalEventType.CANCEL, this.keyboardCancelAction)
    //     this.$GlobalEventBus.off(GlobalEventType.SAVE, this.keyboardSaveAction)
    //     this.$GlobalEventBus.off(GlobalEventType.RECORDING, this.keyboardRecordingAction)
    //     this.$GlobalEventBus.off(GlobalEventType.FAST_ADD, this.keyboardFastAddAction)
    //   })
    // }
    // 静态缓存，或中途被 addDynamicAliveRoutes 收进缓存后再回来
    if (this.isDetailGlobalKeyboardCached()) {
      this.bindDetailGlobalKeyboard()
      this.$once('hook:deactivated', () => {
        this.unbindDetailGlobalKeyboard()
      })
    }
  },

  mounted() {
    this.wrapAllKeyboardActions() // 自动封装 `keyboard` 方法
    // 2026/09/20 原因：非缓存页只在 beforeDestroy 卸监听。被动态收进 keep-alive 后离开不销毁，
    // ESC 会留在采购单上，切到收银再按 ESC 会关掉收银并退回采购单。离开时补一次 deactivated 卸载。
    // 旧代码：
    // if (!keepAliveRoutes.includes(this.$route.name)) {
    //   this.$GlobalEventBus.on(GlobalEventType.CANCEL, this.keyboardCancelAction)
    //   this.$GlobalEventBus.on(GlobalEventType.SAVE, this.keyboardSaveAction)
    //   this.$GlobalEventBus.on(GlobalEventType.RECORDING, this.keyboardRecordingAction)
    //   this.$GlobalEventBus.on(GlobalEventType.FAST_ADD, this.keyboardFastAddAction)
    //   this.$once('hook:beforeDestroy', () => {
    //     this.$GlobalEventBus.off(GlobalEventType.CANCEL, this.keyboardCancelAction)
    //     this.$GlobalEventBus.off(GlobalEventType.SAVE, this.keyboardSaveAction)
    //     this.$GlobalEventBus.off(GlobalEventType.RECORDING, this.keyboardRecordingAction)
    //     this.$GlobalEventBus.off(GlobalEventType.FAST_ADD, this.keyboardFastAddAction)
    //   })
    // }
    // 未使用 keep-alive 缓存
    if (!keepAliveRoutes.includes(this.$route.name)) {
      this.bindDetailGlobalKeyboard()
      const unbind = () => this.unbindDetailGlobalKeyboard()
      this.$once('hook:beforeDestroy', unbind)
      // 录入更多信息会把本页动态塞进 keep-alive，离开只 deactivated、不销毁，必须在这里卸掉 ESC
      this.$once('hook:deactivated', unbind)
    }
  },

  methods: {
    /** 静态 keep-alive，或中途被 addDynamicAliveRoutes 收进缓存 */
    isDetailGlobalKeyboardCached() {
      const name = this.$route?.name
      if (!name) return false
      if (keepAliveRoutes.includes(name)) return true
      const dynamic = this.$store?.getters?.['sys/dynamicAliveRoutes'] || []
      return dynamic.includes(name)
    },
    bindDetailGlobalKeyboard() {
      // 先按上次登记的函数卸掉，避免 mounted 与 activated 各绑一次，或 wrap 后函数引用变了卸不掉
      this.unbindDetailGlobalKeyboard()
      const handlers = {
        cancel: this.keyboardCancelAction,
        save: this.keyboardSaveAction,
        recording: this.keyboardRecordingAction,
        fastAdd: this.keyboardFastAddAction,
      }
      this._detailGlobalKeyboardHandlers = handlers
      this.$GlobalEventBus.on(GlobalEventType.CANCEL, handlers.cancel)
      this.$GlobalEventBus.on(GlobalEventType.SAVE, handlers.save)
      this.$GlobalEventBus.on(GlobalEventType.RECORDING, handlers.recording)
      this.$GlobalEventBus.on(GlobalEventType.FAST_ADD, handlers.fastAdd)
    },
    unbindDetailGlobalKeyboard() {
      const handlers = this._detailGlobalKeyboardHandlers
      if (!handlers) return
      this.$GlobalEventBus.off(GlobalEventType.CANCEL, handlers.cancel)
      this.$GlobalEventBus.off(GlobalEventType.SAVE, handlers.save)
      this.$GlobalEventBus.off(GlobalEventType.RECORDING, handlers.recording)
      this.$GlobalEventBus.off(GlobalEventType.FAST_ADD, handlers.fastAdd)
      this._detailGlobalKeyboardHandlers = null
    },
    // 返回列表/取消
    keyboardCancelAction() {
      if (!this.$refs.cancel?.disabled) {
        this.$refs.cancel?.$el.dispatchEvent(new Event('click'))
      }
    },
    // 保存
    keyboardSaveAction() {
      if (!this.$refs.save?.disabled) {
        this.$refs.save?.$el.dispatchEvent(new Event('click'))
      }
    },
    // 入账
    keyboardRecordingAction() {
      if (!this.$refs.recording?.disabled) {
        this.$refs.recording?.$el.dispatchEvent(new Event('click'))
      }
    },
    // 快速添加
    keyboardFastAddAction() {
      let fastAddComponents = []
      const findFastAdd = (components) => {
        if (!components?.length) return

        for (const component of components) {
          if (!component.$refs) return

          if (component.$refs?.fastAdd) {
            fastAddComponents.push(component)
          }
          findFastAdd(Object.values(component.$refs ?? []))
        }
      }

      if (this.$refs.fastAdd) {
        const inputEl = Array.from(this.$refs.fastAdd?.$el.querySelectorAll('input.el-input__inner')).filter(
          (item) => !item.classList.contains('suggestion-dialog__input')
        )
        inputEl[0].focus()
      } else {
        // 针对组件深层嵌套的页面，递归查找快速添加组件
        findFastAdd(Object.values(this.$refs ?? []))
        if (!fastAddComponents.length) return

        // 若存在多个快速添加组件，则根据快速添加父组件是否在可视窗口内判断聚焦
        const focusComponent = fastAddComponents.toReversed().filter((fastAdd) => {
          const react = fastAdd.$el.getBoundingClientRect()
          return react.bottom > 0 && (react.bottom <= window.innerHeight || document.documentElement.clientHeight)
        })[0]

        // 聚焦快速添加组件中的输入框
        const inputEl = Array.from(
          focusComponent?.$refs?.fastAdd?.$el.querySelectorAll('input.el-input__inner')
        ).filter((item) => !item.classList.contains('suggestion-dialog__input'))
        inputEl[0].focus()
      }
    },
  },
}
Object.assign(GlobalKeyboardDetailActionMixin.methods, commonMethods)
/**
 * 组装 cacheKey
 */

export const CacheKey = {
  computed: {
    ...mapGetters({
      belongCode: 'user/belongCode',
      employeeNo: 'user/employeeNo',
    }),

    cacheKey() {
      return (billType, eventType = '') => {
        return [this.belongCode, billType, this.employeeNo, eventType].join('_')
      }
    },
  },
}
/**
 * 全局权限
 */
export const GlobalPermissionMixin = {
  computed: {
    // 是否禁用展示成本价
    isDisabledGlobalPermissionCostPrice() {
      const route = this.$router.match('/globalFiledPermission')
      if (route.name === '404') return true
      return !route.meta?.buttons?.some((n) => n.path === 'costPrice')
    },
  },
}

/**
 * 本地化样式适配
 */

const langArray = ['zh-CN', 'en-US', 'id', 'th-TH']
export const AdaptLocaleStyle = {
  methods: {
    setLabelWidthByLang(labelWidths) {
      let index = langArray.findIndex((lang) => lang === this.$store.state.sys.lang)

      // 除中文和英文之外的定制化样式都按照英文的
      if (!['zh-CN', 'eh-US'].includes(this.$store.state.sys.lang)) index = 1

      const pxValue =
        labelWidths[index].toString().indexOf('px') > -1 ? labelWidths[index].split('px')[0] : labelWidths[index]

      return isNaN(pxValue) ? pxValue : this.$pxToRem(pxValue)
    },
  },
}


/**
 * 初始化打印模板
 */
export const printMixin = {
  data() {
    return {
      printParamsObj: {
        printOptions: [],
        printPreviewOptions: [],
        printAssignOptions: [],
        printPreviewAssignOptions: [],
        aggregationParams: null,
      },
    }
  },
  methods: {
    /**
     * 获取模板数据
     *
     * @param { String } billType 单据类型
     * @param { String } billIdField 单据类型id表示的字段
     * @param { Array } codeJointNames 需要code-name拼接处理的字段集合 比如：[{ name: '***name', code: '***Code' }]
     * @param { String } barcodeField 单据需要条形码的单据字段名
     * @param { Array } formatDateFields 需要格式化日期的字段名
     * @returns { printOptions,  printPreviewOptions } 返回直接和预览打印各自数据集
     * @param { Array } usePrintTemplates 多模板是指定使用某个模板
     * handleCommandDetail 模板监听事件
     */
    handlePrintTemplateData({
      billType = '',
      billIdField = '',
      codeJointNames = [],
      formatDateFields = [],
      barcodeField = '',
      boxNoField = '',
      boxNoSeachField = '',
      billNoField = '',
      usePrintTemplates = [],
      sortBy = '',
      isTasksPrint = false,
      ids = [],
    } = {}) {
      const printParamsObj = this.printParamsObj
      if (!billType || !billIdField) return
      return new Promise((resolve, reject) => {
        const paramsObj = {
          page: 1,
          size: 10000,
          billTypes: [billType],
          statuses: ['ENABLE'],
        }
        getPrintTemplateList(paramsObj)
          .then((res) => {
            const { data } = res

            // 直接
            printParamsObj.printAssignOptions =
              (data?.items &&
                data?.items?.map((item) => {
                  return {
                    ...item,
                    label: item.templateName,
                    value: item.templateContent,
                    printWay: 'direct',
                    event: (row, command) =>
                      this.handleCommandDetail({
                        ids: ids?.length > 0 ? ids : [row[billIdField]],
                        row,
                        command,
                        type: 'direct',
                        billType,
                        billIdField,
                        codeJointNames,
                        formatDateFields,
                        barcodeField,
                        billNoField,
                        boxNoField,
                        boxNoSeachField,
                        sortBy,
                        isTasksPrint,
                      }),
                  }
                })) ||
              []
            printParamsObj.printOptions =
              usePrintTemplates.length > 0
                ? printParamsObj.printAssignOptions.filter((item) => usePrintTemplates.includes(item.type))
                : printParamsObj.printAssignOptions

            // 预览
            printParamsObj.printPreviewAssignOptions =
              (data?.items &&
                data?.items?.map((item) => {
                  return {
                    ...item,
                    label: item.templateName,
                    value: item.templateContent,
                    printWay: 'preview',
                    event: (row, command) =>
                      this.handleCommandDetail({
                        ids: ids?.length > 0 ? ids : [row[billIdField]],
                        row,
                        command,
                        type: 'preview',
                        billType,
                        billIdField,
                        codeJointNames,
                        formatDateFields,
                        barcodeField,
                        billNoField,
                        boxNoField,
                        boxNoSeachField,
                        sortBy,
                        isTasksPrint,
                      }),
                  }
                })) ||
              []
            printParamsObj.printPreviewOptions =
              usePrintTemplates.length > 0
                ? printParamsObj.printPreviewAssignOptions.filter((item) => usePrintTemplates.includes(item.type))
                : printParamsObj.printPreviewAssignOptions
            resolve({
              printOptions: printParamsObj.printOptions,
              printPreviewOptions: printParamsObj.printPreviewOptions,
            })
          })
          .catch((err) => {
            reject(err)
          })
      })
    },
    /**
     * 触发打印
     * @param { Array }  batchPrintBillType 批量打印 需要入参ids的单据类型集合
     * @param { Array }  ids 批量打印id集合
     * @param { Object } row 当前行数据
     * @param { Object } command 当前触发的打印模板
     * @param { String } type 打印类型 可选值: { 直接打印: direct; 预览打印: preview }
     * @param { String } billType 单据类型
     * @param { String } billIdField 单据类型id字段名
     * @param { Array } codeJointNames 需要code-name拼接处理的字段名
     * @param { Array } formatDateFields 需要格式化日期的字段名
     * @param { String } barcodeField 单据需要条形码的单据字段名
     * @param { String } boxNoField 复核单箱号字段名
     * @param { String } billNoField 单号字段名
     * @param { Boolean } isRedrawSetDesign 是否重新绘制模板
     * @param { Boolean } isTasksPrint 是否是队列打印
     * @param { String } sortBy 明细排序字段
     * @param { String } mainSortBy 主表排序字段
     */
    handleCommandDetail({
      batchPrintBillType = ['SALE_ORDER'],
      ids = [],
      row = null,
      command = null,
      type = '',
      billType = '',
      billIdField = '',
      codeJointNames = [],
      formatDateFields = [],
      barcodeField = '',
      boxNoField = '',
      boxNoSeachField = '',
      billNoField = '',
      isRefresh = true,
      sortBy = '',
      isRedrawSetDesign = false,
      isTasksPrint = false,
      mainSortBy = '',
    } = {}) {
      const __that = this
      const dayJs = this.dayjs
      if (!row || !command || !type || !billType || !billIdField)
        return this.$mgMessage(i18n.t('打印所需参数异常，请联系管理员核对后重试'), 'error')
      const printPanels = (command?.templateContent && JSON.parse(command?.templateContent)) || null
      if (!printPanels) return this.$mgMessage(i18n.t('该模板异常，请核对后重试'), 'error')
      return new Promise((resolve, reject) => {
        const printParamsObj = this.printParamsObj

        // 单次打印
        const singlePrintParams = {
          billTypeEnum: billType,
          boxNo: (boxNoSeachField && row[boxNoSeachField]) || row[boxNoField],
          // 兼容没有批量打印的单据，故2个都传，后端根据需要取值
          ids: [row[billIdField]],
          id: row[billIdField],
          saleOrderNo: row[billNoField],
          sortBy,
        }
        // 批量打印
        const batchPrintParams = {
          billTypeEnum: billType,
          ids,
          sortBy,
          mainSortBy,
        }
        const aggregationParams =
          ids?.length > 0 && batchPrintBillType.includes(billType) ? batchPrintParams : singlePrintParams
        printParamsObj.aggregationParams = aggregationParams
        handlePrintData(aggregationParams)
          .then((printDataRes) => {
            /**
             * 单次打印数据处理 -- 开始
             */
            const data =
              Object.prototype.toString.call(printDataRes?.data) === '[object Array]'
                ? printDataRes?.data?.find((item) => item[billIdField] === row[billIdField])
                : printDataRes?.data
            let printData = {},
              printDetailData = []
            if (data) {
              const printLists =
                data?.items?.map((item, i) => {
                  return {
                    ...item,
                    index: i + 1,
                  }
                }) || []
              // 需要拼接的字段
              const codeJointedNames = {}
              codeJointNames.map((item) => {
                const newItems = {
                  [item.name]:
                    data[item.code] && data[item.name]
                      ? `${data[item.code]}-${data[item.name]}`
                      : data[item.name] || '',
                }
                Object.assign(codeJointedNames, newItems)
              })
              // 日期
              const formatDates = {}
              formatDateFields.map((item) => {
                const newItems = {
                  [item]: data[item] && dayJs(data[item]).format('YYYY-MM-DD HH:mm:ss'),
                }
                Object.assign(formatDates, newItems)
              })
              // 条形码
              const barcode = {}
              if (barcodeField) Object.assign(barcode, { barcode: data[barcodeField] })

              // 主表 + 明细的数据处理
              printData = {
                ...data,
                ...codeJointedNames,
                ...formatDates,
                ...barcode,
                [boxNoField]: row[boxNoField],
                printTime: dayJs().format('YYYY-MM-DD HH:mm:ss'),
                printLists,
              }

              // 纯明细数据处理
              printDetailData = printLists
            }
            /**
             * 单次打印数据处理 -- 结束
             */

            // 预览打印
            this.handlePrintInit(printPanels).then(async (res) => {
              if (res) {
                if (type === 'direct' && !window?.hiwebSocket.opened)
                  return this.$mgMessage(i18n.t('客户端未连接,无法直接打印'), 'error')
                // 直接打印
                if (type === 'direct' && window?.hiwebSocket.opened) {
                  if (printPanels?.panels) {
                    const { width, height } = printPanels.panels[0]
                    const printPageSize = {
                      width: BN.times(width, 1000).toNumber(),
                      height: BN.times(BN.minus(height, 1), 1000).toNumber(),
                    }
                    const printerList = res.getPrinterList()
                    // 当前单据类型
                    const currentBillTypeItem = modules.common.templateJsonOptions.find(
                      (item) => item.value === command.billType
                    )
                    const printTaskTitle = i18n.t('晨光-{slot1}-{slot2}', {
                      slot1: currentBillTypeItem.label,
                      slot2: command.templateName,
                    })

                    // 直接打印所需额外参数
                    const print2Params = {
                      printer: command?.defaultPrinterName || printerList,
                      title: printTaskTitle,
                      pageSize: printPageSize,
                      styleHandler: () => {
                        const css =
                          '<link href="../../public/static/css/print-lock.css" media="print" rel="stylesheet">'
                        return css
                      },
                    }

                    /**
                     * printer: 打印机列表
                     * title:   打印任务标题
                     * pageSize: 因为这里的pageSize是对electronj开发的客户端设置的打印纸张规格,
                     * 其所用的单位是μm, 而插件hiPrint打印的纸张规格用的是mm
                     * 所以需要用mm去换算μm(1mm === 1000μm)
                     */
                    if (isTasksPrint && !printDataRes.data?.length)
                      return this.$mgMessage(i18n.t('打印数据为空，请核对后重试'), 'warning')

                    // 是否走队列打印
                    if (isTasksPrint && printDataRes.data?.length > 0) {
                      /**
                       * 批量打印数据处理 -- 开始
                       */
                      const batchPrintData = printDataRes?.data.map((printDataResItem) => {
                        // 需要拼接的字段
                        const codeJointedNames = {}
                        codeJointNames.map((item) => {
                          const newItems = {
                            [item.name]:
                              printDataResItem[item.code] && printDataResItem[item.name]
                                ? `${printDataResItem[item.code]}-${printDataResItem[item.name]}`
                                : printDataResItem[item.name] || '',
                          }
                          Object.assign(codeJointedNames, newItems)
                        })
                        // 日期
                        const formatDates = {}
                        formatDateFields.map((item) => {
                          const newItems = {
                            [item]:
                              printDataResItem[item] && dayJs(printDataResItem[item]).format('YYYY-MM-DD HH:mm:ss'),
                          }
                          Object.assign(formatDates, newItems)
                        })
                        // 条形码
                        const barcode = {}
                        if (barcodeField) Object.assign(barcode, { barcode: printDataResItem[barcodeField] })

                        return {
                          ...printDataResItem,
                          ...codeJointedNames,
                          ...formatDates,
                          ...barcode,
                          [boxNoField]: row[boxNoField],
                          printTime: dayJs().format('YYYY-MM-DD HH:mm:ss'),
                          printLists:
                            printDataResItem?.items?.map((item, i) => {
                              return {
                                ...item,
                                index: i + 1,
                              }
                            }) || [],
                        }
                      })
                      /**
                       * 批量打印数据处理 -- 结束
                       */
                      __that
                        .handleTasksPrint({
                          printLists: command?.templateType === 'BILL' ? batchPrintData : printDetailData,
                          print2Params,
                        })
                        .then((taskPrintRes) => {
                          if (aggregationParams.billTypeEnum === 'SALE_ORDER') {
                            resolve(taskPrintRes)
                          } else if (taskPrintRes.tasks.length === 0) {
                            resolve(taskPrintRes)
                          }
                        })
                        .catch((err) => {
                          reject(err)
                        })
                        .finally(() => {
                          __that.handlePrintSuccess({ data: aggregationParams, isRefresh })
                        })
                    } else {
                      await res.print2(command?.templateType === 'BILL' ? printData : printDetailData, print2Params)
                      await __that.$mgMessage(i18n.t('已连接打印机, 请勿频繁操作'), 'warning')
                      await __that.handlePrintSuccess({ data: aggregationParams, isRefresh })
                      res.on('printSuccess', function () {
                        __that.$mgMessage(i18n.t('正在打印中, 请稍候'), 'success')
                      })
                    }
                  }
                }

                // 预览打印
                if (type === 'preview') {
                  this.handlePrintPreView(
                    command?.templateType === 'BILL' ? printData : printDetailData,
                    'printPreviewRef'
                  )
                  // 是否重绘
                  if (isRedrawSetDesign) this?.$refs['mgPrintParentConRef']?.handleSetDesign()
                }
              }
            })
          })
          .catch((error) => {
            console.log(error, '获取打印数据接口有误！！！')
          })
      })
    },
    /**
     * 更新打印次数
     * 为了避免有的单据定义的表格ref属性不同，所以加一变量来兼容
     * @param { String } tableRef 表格的ref 默认值为：mgTable
     */
    async handlePrintSuccess({ tableRef = 'mgTable', data = null, isRefresh = true } = {}) {
      const printParamsObj = this.printParamsObj
      if (!data && !printParamsObj.aggregationParams)
        return this.$mgMessage(i18n.t('更新打印次数所需参数有误，请核对后重试'), 'error')
      try {
        const aggregationParams = data || printParamsObj.aggregationParams
        await handleUpdataPrintData(aggregationParams)
        if (isRefresh) {
          await this.$nextTick(() => {
            this.$refs[tableRef]?.loadData()
          })
        }
      } catch (error) {
        throw new Error(error)
      }
    },
  },
}

/**
 * 全局快捷键操作-操作页
 */
export const GlobalKeyboardActionMixinForOperate = {
  activated() {
    this.$GlobalEventBus.on(GlobalEventType.CANCEL, this.keyboardCancelAction)
    this.$GlobalEventBus.on(GlobalEventType.SAVE, this.keyboardSaveAction)
    this.$GlobalEventBus.on(GlobalEventType.AUDIT, this.keyboardAuditAction)
    this.$GlobalEventBus.on(GlobalEventType.RECORDING, this.keyboardRecordingAction)
    this.$GlobalEventBus.on(GlobalEventType.FAST_ADD, this.keyboardFastAddAction)
    this.$GlobalEventBus.on(GlobalEventType.CLEAR_ITEMS, this.keyboardClearItemsAction)
    this.$GlobalEventBus.on(GlobalEventType.PRINT, this.keyboardPrintAction)
    this.$GlobalEventBus.on(GlobalEventType.CUSTOMER_CHANGE, this.keyboardCustomerChange)
    this.$once('hook:deactivated', () => {
      this.$GlobalEventBus.off(GlobalEventType.CANCEL, this.keyboardCancelAction)
      this.$GlobalEventBus.off(GlobalEventType.SAVE, this.keyboardSaveAction)
      this.$GlobalEventBus.off(GlobalEventType.AUDIT, this.keyboardAuditAction)
      this.$GlobalEventBus.off(GlobalEventType.RECORDING, this.keyboardRecordingAction)
      this.$GlobalEventBus.off(GlobalEventType.FAST_ADD, this.keyboardFastAddAction)
      this.$GlobalEventBus.off(GlobalEventType.CLEAR_ITEMS, this.keyboardClearItemsAction)
      this.$GlobalEventBus.off(GlobalEventType.PRINT, this.keyboardPrintAction)
      this.$GlobalEventBus.off(GlobalEventType.CUSTOMER_CHANGE, this.keyboardCustomerChange)
    })
  },
  methods: {
    // 返回列表/取消
    keyboardCancelAction() {
      if (!this.$refs.cancel?.disabled) {
        this.$refs.cancel?.$el.dispatchEvent(new Event('click'))
      }
    },
    // 保存
    keyboardSaveAction() {
      if (!this.$refs.save?.disabled) {
        this.$refs.save?.$el.dispatchEvent(new Event('click'))
      }
    },
    // 审核
    keyboardAuditAction() {
      if (!this.$refs.audit?.disabled) {
        this.$refs.audit?.$el.dispatchEvent(new Event('click'))
      }
    },
    // 入账
    keyboardRecordingAction() {
      if (!this.$refs.recording?.disabled) {
        this.$refs.recording?.$el.dispatchEvent(new Event('click'))
      }
    },
    // 快速添加
    keyboardFastAddAction() {
      if (!this.$refs.fastAddBtn?.disabled) {
        this.$refs.fastAddBtn?.$el.dispatchEvent(new Event('click'))
      }
    },
    // 清空明细
    keyboardClearItemsAction() {
      if (!this.$refs.clearItems?.disabled) {
        this.$refs.clearItems?.$el.dispatchEvent(new Event('click'))
      }
    },
    // 打印
    keyboardPrintAction() {
      if (!this.$refs.print?.disabled) {
        this.$refs.print?.$el.querySelector('button').dispatchEvent(new Event('click'))
      }
    },
    // 更换客户
    keyboardCustomerChange() {
      if (!this.$refs.customerChange?.disabled) {
        this.$refs.customerChange?.$el.dispatchEvent(new Event('click'))
      }
    },
  },
}